import React, { useState } from 'react';
import { Bookmark } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEditorStore } from '../../store/editorStore';
import { exportConfigsApi } from '../../api/exportConfigs';
import { track, EVENTS } from '../../lib/mixpanel';
import { Button } from '../UI/Button';
import styles from './SaveConfigButton.module.css';

interface SaveConfigButtonProps {
  eventId: string;
}

export const SaveConfigButton: React.FC<SaveConfigButtonProps> = ({ eventId }) => {
  const { filters, overlayConfig, selectedPlatforms, selectedFormats } = useEditorStore();
  const [showInput, setShowInput] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Enter a config name');
      return;
    }

    setSaving(true);
    try {
      await exportConfigsApi.create(eventId, {
        name: name.trim(),
        filters,
        overlay: overlayConfig,
        selectedPlatforms,
        selectedFormats,
      });
      track(EVENTS.EXPORT_CONFIG_SAVED, {
        event_id: eventId,
        config_name: name.trim(),
        platform_count: selectedPlatforms.length,
        format_count: selectedFormats.length,
      });
      toast.success('Config saved!');
      setShowInput(false);
      setName('');
    } catch {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  if (!showInput) {
    return (
      <button
        onClick={() => setShowInput(true)}
        className={styles['toggle-link']}
      >
        <Bookmark className={styles['icon']} />
        Save current settings as preset
      </button>
    );
  }

  return (
    <div className={styles['form-row']}>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Config name..."
        className={styles['name-input']}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') setShowInput(false);
        }}
      />
      <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
        Save
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setShowInput(false)}>
        Cancel
      </Button>
    </div>
  );
};
